// Audit Logger with duplicate suppression (Backend-V1 upgrade readiness)
// 
// This module implements a central audit logging system with:
// - Duplicate suppression (configurable window, default 1200ms)
// - Server POST capability (flag-gated by ENABLE_SERVER_ACTIVITY_LOG)
// - Integration with existing systemLog for local storage
// - Entry validation (requires action and user fields)
// - Optional async server dispatch
// - Schema versioning for future compatibility
// - Pre-persist transformation hook for redaction
//
// Features implemented:
// - recordAudit: Central function with suppression logic
// - getSuppressionKey: Generate deduplication keys
// - sendServerAudit: Async server POST (placeholder for future backend endpoint)
// - prePersistTransform: Hook for sensitive field redaction

import { addLog } from './systemLog';
import { ENABLE_SERVER_ACTIVITY_LOG } from '../config/flags';

/**
 * @typedef {Object} AuditLogEntry
 * @property {number} id - Unique identifier (timestamp + random suffix)
 * @property {string} time - ISO 8601 timestamp
 * @property {number} schema - Schema version number
 * @property {string} action - Activity action type (from ACTIVITY_ACTION constants)
 * @property {string} user - User display name who performed the action
 * @property {string} [customer] - Customer name (for booking-scoped actions)
 * @property {string} [bookingId] - Booking identifier
 * @property {string} [from] - Previous value (for changes)
 * @property {string} [to] - New value (for changes)
 * @property {string} [text] - Additional text content (e.g., notes, truncated to 300 chars)
 */

// Schema version for audit log entries
export const LOG_SCHEMA_VERSION = 1;

// Fallback user name when user info is unavailable
export const FALLBACK_USER_NAME = 'Unknown User';

/**
 * Reads a numeric environment variable from multiple sources
 * @param {string[]} keys - Array of environment variable keys to check
 * @param {number} defaultValue - Default value if no key is found
 * @returns {number} The resolved numeric value
 */
function readNumEnv(keys, defaultValue) {
  for (const key of keys) {
    // Check import.meta.env (Vite) - use indirect access for test compatibility
    try {
      // eslint-disable-next-line no-eval
      const importMeta = typeof window !== 'undefined' ? eval('typeof import.meta !== "undefined" ? import.meta : undefined') : undefined;
      if (importMeta && importMeta.env && importMeta.env[key]) {
        const val = parseInt(importMeta.env[key], 10);
        if (!isNaN(val) && val > 0) return val;
      }
    } catch (e) {
      // import.meta may not be available in test or non-module environment
    }
    // Check process.env (Create React App / Node)
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      const val = parseInt(process.env[key], 10);
      if (!isNaN(val) && val > 0) return val;
    }
  }
  return defaultValue;
}

// Suppression window: configurable via env, default 1200ms
const SUPPRESSION_WINDOW_MS_DEFAULT = 1200;
export const EFFECTIVE_SUPPRESSION_WINDOW_MS = readNumEnv(
  ['VITE_AUDIT_SUPPRESS_MS', 'REACT_APP_AUDIT_SUPPRESS_MS'],
  SUPPRESSION_WINDOW_MS_DEFAULT
);

// For backward compatibility
export const SUPPRESSION_WINDOW_MS = EFFECTIVE_SUPPRESSION_WINDOW_MS;

// In-memory map to track recent log entries
// Key format: action|bookingId|from|to|text
const recentLogs = new Map();

/**
 * Pre-persist transformation hook for audit entries
 * Override this function to implement custom redaction logic
 * 
 * @param {Object} entry - The audit entry before persistence
 * @returns {Object} Transformed entry (or same entry if no changes)
 * 
 * @example
 * // Future redaction implementation:
 * // if (entry.action === 'PRICE') {
 * //   return { ...entry, from: '[REDACTED]', to: '[REDACTED]' };
 * // }
 */
export let prePersistTransform = (entry) => entry;

/**
 * Set a custom pre-persist transformation hook
 * @param {Function} fn - Transform function (entry) => transformedEntry
 */
export function setPrePersistTransform(fn) {
  if (typeof fn === 'function') {
    prePersistTransform = fn;
  }
}

/**
 * Truncates text for audit log storage
 * @param {string} text - Text to truncate
 * @param {number} max - Maximum length (default: 300)
 * @returns {string} Truncated text with ellipsis if needed
 */
export function truncateForAudit(text, max = 300) {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= max) return text;
  return text.substring(0, max) + '...';
}

/**
 * Generates a suppression key from an audit entry
 */
function getSuppressionKey(entry) {
  const parts = [
    entry.action || '',
    entry.bookingId || '',
    entry.from || '',
    entry.to || '',
    entry.text || ''
  ];
  return parts.join('|');
}

/**
 * Sends audit log entry to server
 * @param {Object} logEntry - The log entry to send to server
 */
async function sendServerAudit(logEntry) {
  // Dynamic import to avoid circular dependency issues
  const { axiosApi } = await import('../service/apiEnviornment');
  
  // Map client log entry to server expected format
  // Include all fields supported by Backend-V1 audit log schema
  const serverEntry = {
    time: logEntry.time,
    action: logEntry.action,
    user: logEntry.user,
    customer: logEntry.customer,
    bookingId: logEntry.bookingId,
    from: logEntry.from,
    to: logEntry.to,
    text: logEntry.text,
    schema: logEntry.schema,
    meta: logEntry.meta,
    // Settings-related fields
    section: logEntry.section,
    changes: logEntry.changes,
    // User management fields
    targetUser: logEntry.targetUser,
    targetEmail: logEntry.targetEmail,
    roles: logEntry.roles,
    rolesDisplay: logEntry.rolesDisplay,
  };
  
  // Remove undefined fields
  Object.keys(serverEntry).forEach(key => {
    if (serverEntry[key] === undefined) {
      delete serverEntry[key];
    }
  });
  
  await axiosApi('POST', `/activity-log`, serverEntry);
}

/**
 * Records an audit entry with duplicate suppression
 * 
 * @param {Object} entry - Audit entry object (action, user, customer, bookingId, etc.)
 * @returns {AuditLogEntry|null} - Returns the log entry if recorded, null if suppressed
 */
export function recordAudit(entry) {
  // Validate required fields
  if (!entry || !entry.action || !entry.user) {
    // Skip malformed entries
    return null;
  }
  
  const key = getSuppressionKey(entry);
  const now = Date.now();
  
  // Check if this entry was recently logged
  if (recentLogs.has(key)) {
    const lastTime = recentLogs.get(key);
    if (now - lastTime < EFFECTIVE_SUPPRESSION_WINDOW_MS) {
      // Suppressed - return null
      return null;
    }
  }
  
  // Update the suppression map
  recentLogs.set(key, now);
  
  // Clean up old entries to prevent memory leaks
  // Run cleanup more aggressively when threshold reached (every ~50 entries)
  // This prevents excessive memory usage and keeps iteration time minimal
  if (recentLogs.size > 50) {
    const cutoff = now - EFFECTIVE_SUPPRESSION_WINDOW_MS;
    const keysToDelete = [];
    
    // Collect keys to delete first to avoid iterator issues
    for (const [mapKey, timestamp] of recentLogs.entries()) {
      if (timestamp < cutoff) {
        keysToDelete.push(mapKey);
      }
    }
    
    // Delete collected keys
    keysToDelete.forEach(k => recentLogs.delete(k));
  }
  
  // Add schema version to entry
  const entryWithSchema = {
    ...entry,
    schema: LOG_SCHEMA_VERSION,
  };
  
  // Apply pre-persist transformation hook
  const transformedEntry = prePersistTransform(entryWithSchema);
  
  // Record to local storage via addLog
  // Note: timestamp and id are added inside addLog
  const logEntry = addLog(transformedEntry);
  
  // Dispatch custom event for same-window real-time updates
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('audit-log-added', { detail: logEntry }));
    } catch (err) {
      // Silently ignore event dispatch errors
    }
  }
  
  // Send to server if flag enabled (fire-and-forget)
  if (ENABLE_SERVER_ACTIVITY_LOG) {
    try {
      sendServerAudit(logEntry).catch((err) => {
        // Silent fail for server logging - don't block client operation
        // eslint-disable-next-line no-console
        console.warn('Failed to send audit log to server:', err);
      });
    } catch (err) {
      // Catch synchronous errors
      // eslint-disable-next-line no-console
      console.warn('Error initiating server audit:', err);
    }
  }
  
  return logEntry;
}

// Development-only helper for console debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__dumpAuditLogs = () => {
    const { getLogs } = require('./systemLog');
    return getLogs();
  };
}
